/**
 * Factory that produces a `ComicSource` for either e-hentai or exhentai.
 *
 * Both sites share 99% of the same logic; this module is the connective
 * tissue between the parsers / api.php / image-flow modules and the
 * `ComicSource` interface that the registry expects. Each variant module
 * (`src/sources/ehentai/index.ts`, `src/sources/exhentai/index.ts`) just
 * picks a `SiteContext` and calls `buildEhSource()`.
 */

import { serializeCookies } from '@/lib/cookies';
import { CloudflareChallenge, NetworkError, ParseError } from '@/lib/errors';
import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  ListOpts,
  Page,
  PagePreview,
  PagePreviewBatch,
  PageRef,
  PopularOpts,
  RecommendationOption,
  RecommendationOpts,
  SearchFiltersSchema,
  SearchQuery,
  SourceAuth,
  SourceCommentOpts,
  SourceCommentPage,
} from '../types';

import {
  buildListUrl,
  type AdvancedSearch,
  type CategoryName,
  categoriesToMask,
  ALL_CATEGORY_NAMES,
} from './list-url';
import { gdata, type GalleryMetadata } from './api-php';
import { parseGalleryList } from './parsers/gallery-list';
import { parseGalleryComments, parseGalleryDetail } from './parsers/gallery-detail';
import { parsePreviewPage, type ParsedPagePreview } from './parsers/preview';
import { parseUConfig } from './parsers/uconfig';
import {
  GALLERY_PAGE_ZERO_PERSIST_TTL_MS,
  GALLERY_PREVIEW_PAGE_PERSIST_TTL_MS,
  readPersistedEhHtml,
  writePersistedEhHtml,
} from './html-cache';
import { galleryUrl, publicEhImageUrl, withNw, type SiteContext, type SiteId } from './urls';
import { fetchEhText } from './network';
import {
  getPTokenForPageFromPreview,
  loadPage,
  loadFirstPage,
  primePTokenCacheFromPreviewHtml,
  type FirstPageData,
} from './image-flow';
import {
  addFavorite as ehAddFavorite,
  listFavorites as ehListFavorites,
  removeFavorite as ehRemoveFavorite,
} from './favorites';

export interface BuildEhSourceOpts {
  site: SiteContext;
  /** SourceId used in the SPA's registry — must match the union in types.ts. */
  sourceId: SiteId;
  /** Human-readable site name. */
  name: string;
  /** Short description for the Home card. */
  description: string;
  /** Optional capability overrides — e-hentai vs exhentai differ on `igneous`. */
  loginNotes?: string;
  /**
   * Extra `validateAuth` step run after the standard `/uconfig.php` check.
   * Used by exhentai to refuse logins that have no `igneous` cookie.
   */
  extraValidate?: (auth: SourceAuth) => Promise<{ ok: boolean; reason?: string } | undefined>;
}

/**
 * In-memory per-gallery cache of `showKey` + first-page image data.
 * showKey rotates ~every 30 minutes server-side; we drop ours after 25.
 */
const SHOW_KEY_TTL_MS = 25 * 60 * 1000;

interface ShowKeyCacheEntry {
  showKey: string;
  /** Cached image URL for page 1 (saves one round-trip if the user jumps back). */
  firstPageImageUrl?: string;
  /** Skip-Hath key from the page-1 scrape. */
  skipHathKey?: string;
  expiresAt: number;
}

const showKeyCache = new Map<string, ShowKeyCacheEntry>();
const showKeyInFlight = new Map<string, Promise<ShowKeyCacheEntry>>();
const GALLERY_METADATA_TTL_MS = 5 * 60 * 1000;

const EH_RECOMMENDATION_OPTIONS: RecommendationOption[] = [
  { id: 'popular', label: 'Popular', default: true },
  { id: 'toplist-day', label: 'Toplist: Day' },
  { id: 'toplist-month', label: 'Toplist: Month' },
  { id: 'toplist-year', label: 'Toplist: Year' },
  { id: 'toplist-alltime', label: 'Toplist: All time' },
];

const EH_TOPLIST_IDS: Record<string, number> = {
  'toplist-day': 13,
  'toplist-month': 12,
  'toplist-year': 11,
  'toplist-alltime': 1,
};

interface GalleryMetadataCacheEntry {
  row?: GalleryMetadata;
  expiresAt: number;
}

const galleryMetadataCache = new Map<string, GalleryMetadataCacheEntry>();
const galleryMetadataInFlight = new Map<string, Promise<GalleryMetadata | undefined>>();

function showKeyCacheKey(siteId: SiteId, gid: number | string): string {
  return `${siteId}:${gid}`;
}

function galleryMetadataCacheKey(siteId: SiteId, gid: number | string, token: string): string {
  return `${siteId}:${gid}:${token}`;
}

const GALLERY_HTML_TTL_MS = 5 * 60_000;
const GALLERY_HTML_CACHE_MAX_ENTRIES = 32;

interface GalleryHtmlCacheEntry {
  html: string;
  url: string;
  expiresAt: number;
}

const galleryHtmlCache = new Map<string, GalleryHtmlCacheEntry>();
const galleryHtmlInFlight = new Map<string, Promise<GalleryHtmlCacheEntry>>();

async function galleryAuthScope(cookies: string): Promise<string> {
  if (!cookies) return 'anonymous';
  const digest = new Uint8Array(
    await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(cookies)),
  );
  let hex = '';
  for (const byte of digest) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

async function loadGalleryPageZero(
  site: SiteContext,
  gid: number,
  token: string,
  cookies: string,
): Promise<GalleryHtmlCacheEntry> {
  const authScope = await galleryAuthScope(cookies);
  const key = `${site.id}:${gid}:${token}:${authScope}`;
  const cached = galleryHtmlCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached;
  if (cached) galleryHtmlCache.delete(key);

  const pending = galleryHtmlInFlight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    const persisted = await readPersistedEhHtml(key);
    let entry: GalleryHtmlCacheEntry;
    if (persisted) {
      primePTokenCacheFromPreviewHtml(gid, site, persisted.html);
      entry = {
        html: persisted.html,
        url: persisted.url,
        expiresAt: Math.min(persisted.expiresAt, Date.now() + GALLERY_HTML_TTL_MS),
      };
    } else {
      const url = withNw(galleryUrl(site, gid, token), site);
      const fetchOpts: { cookies?: string } = {};
      if (cookies) fetchOpts.cookies = cookies;
      const html = await fetchEhText(url, site, fetchOpts);
      primePTokenCacheFromPreviewHtml(gid, site, html);
      entry = { html, url, expiresAt: Date.now() + GALLERY_HTML_TTL_MS };
      void writePersistedEhHtml(key, { html, url }, GALLERY_PAGE_ZERO_PERSIST_TTL_MS);
    }
    if (galleryHtmlCache.size >= GALLERY_HTML_CACHE_MAX_ENTRIES) {
      const oldestKey = galleryHtmlCache.keys().next().value;
      if (typeof oldestKey === 'string') galleryHtmlCache.delete(oldestKey);
    }
    galleryHtmlCache.set(key, entry);
    return entry;
  })().finally(() => {
    galleryHtmlInFlight.delete(key);
  });
  galleryHtmlInFlight.set(key, promise);
  return promise;
}

function mapEhPagePreview(args: {
  parsed: ParsedPagePreview;
  id: string;
  gid: number;
  token: string;
  previewPage: number;
  site: SiteContext;
}): PagePreview {
  const thumbnail =
    args.parsed.thumbnail.kind === 'image'
      ? {
          ...args.parsed.thumbnail,
          url: publicEhImageUrl(args.parsed.thumbnail.url, args.site),
        }
      : {
          ...args.parsed.thumbnail,
          url: publicEhImageUrl(args.parsed.thumbnail.url, args.site),
        };
  const preview: PagePreview = {
    comicId: args.id,
    index: args.parsed.page - 1,
    page: args.parsed.page,
    thumbnail,
    extra: {
      gid: args.gid,
      token: args.token,
      ptoken: args.parsed.ptoken,
      previewPage: args.previewPage,
    },
  };
  if (args.parsed.label !== undefined) preview.label = args.parsed.label;
  return preview;
}

function buildPagePreviewBatch(args: {
  id: string;
  gid: number;
  token: string;
  previewPage: number;
  site: SiteContext;
  html: string;
  pageUrl: string;
}): PagePreviewBatch | undefined {
  const parsed = parsePreviewPage(args.html, { pageUrl: args.pageUrl });
  if (parsed.previews.length === 0) return undefined;
  const items = parsed.previews.map((preview) =>
    mapEhPagePreview({
      parsed: preview,
      id: args.id,
      gid: args.gid,
      token: args.token,
      previewPage: args.previewPage,
      site: args.site,
    }),
  );
  const maxPreviewPage = Math.max(...items.map((item) => item.page));
  const hasMoreByItemCount =
    parsed.galleryPageCount !== undefined && maxPreviewPage < parsed.galleryPageCount;
  const hasMoreByPreviewPages =
    parsed.totalPreviewPages !== undefined && args.previewPage + 1 < parsed.totalPreviewPages;
  const hasMore = hasMoreByItemCount || hasMoreByPreviewPages;
  const batch: PagePreviewBatch = {
    items,
    previewPage: args.previewPage,
    hasMore,
  };
  if (hasMore) batch.nextPreviewPage = args.previewPage + 1;
  if (parsed.galleryPageCount !== undefined) batch.totalItems = parsed.galleryPageCount;
  return batch;
}

function getShowKey(siteId: SiteId, gid: number | string): ShowKeyCacheEntry | undefined {
  const e = showKeyCache.get(showKeyCacheKey(siteId, gid));
  if (!e) return undefined;
  if (e.expiresAt <= Date.now()) {
    showKeyCache.delete(showKeyCacheKey(siteId, gid));
    return undefined;
  }
  return e;
}

function putShowKey(siteId: SiteId, gid: number | string, data: FirstPageData): ShowKeyCacheEntry {
  const entry: ShowKeyCacheEntry = {
    showKey: data.showKey,
    firstPageImageUrl: data.imageUrl,
    expiresAt: Date.now() + SHOW_KEY_TTL_MS,
  };
  if (data.skipHathKey !== undefined) entry.skipHathKey = data.skipHathKey;
  showKeyCache.set(showKeyCacheKey(siteId, gid), entry);
  return entry;
}

/**
 * Parse the `gid:token` ID used throughout the SPA. Returns numeric gid
 * for downstream API calls. Accepts the legacy `gid_token` form too in
 * case any older history rows are floating around.
 */
export function parseEhId(id: string): { gid: number; token: string } {
  const m = /^(\d+)[:_]([0-9a-z]+)$/i.exec(id.trim());
  if (!m) throw new ParseError(`invalid eh id: ${id}`);
  const gid = Number.parseInt(m[1] ?? '0', 10);
  if (!Number.isFinite(gid) || gid <= 0) {
    throw new ParseError(`invalid eh gid in id: ${id}`);
  }
  return { gid, token: m[2] ?? '' };
}

const CATEGORY_ALIASES: Record<string, CategoryName> = {
  misc: 'misc',
  doujinshi: 'doujinshi',
  manga: 'manga',
  'artist cg': 'artistcg',
  artistcg: 'artistcg',
  'game cg': 'gamecg',
  gamecg: 'gamecg',
  'image set': 'imageset',
  imageset: 'imageset',
  cosplay: 'cosplay',
  'asian porn': 'asianporn',
  asianporn: 'asianporn',
  'non-h': 'nonh',
  nonh: 'nonh',
  western: 'western',
};

function categoryNamesFromQuery(q: SearchQuery): CategoryName[] {
  const names: CategoryName[] = [];
  // Legacy path: include tags with a `category:` prefix.
  for (const tag of q.includeTags ?? []) {
    if (!tag.toLowerCase().startsWith('category:')) continue;
    const raw = tag.slice('category:'.length).trim().toLowerCase();
    const name = CATEGORY_ALIASES[raw];
    if (name) names.push(name);
  }
  // Filter-drawer path: `q.extra.categories` produced by the UI.
  const cats = q.extra?.['categories'];
  if (Array.isArray(cats)) {
    for (const raw of cats) {
      if (typeof raw !== 'string') continue;
      const name = CATEGORY_ALIASES[raw.toLowerCase()];
      if (name && !names.includes(name)) names.push(name);
    }
  }
  return names;
}

function coerceNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Read source-specific filter values out of {@link SearchQuery.extra}. */
function advancedFromQuery(q: SearchQuery): AdvancedSearch | undefined {
  const e = q.extra;
  if (!e) return undefined;
  const adv: AdvancedSearch = {};
  let touched = false;
  const minRating = coerceNumber(e['minRating']);
  if (minRating !== undefined) {
    adv.minRating = minRating;
    touched = true;
  }
  const minPages = coerceNumber(e['minPages']);
  if (minPages !== undefined) {
    adv.minPages = minPages;
    touched = true;
  }
  const maxPages = coerceNumber(e['maxPages']);
  if (maxPages !== undefined) {
    adv.maxPages = maxPages;
    touched = true;
  }
  if (e['sh'] === true) {
    adv.sh = true;
    touched = true;
  }
  if (e['sr'] === true) {
    adv.sto = true;
    touched = true;
  }
  for (const key of ['sfl', 'sfu', 'sft'] as const) {
    if (e[key] === true) {
      adv[key] = true;
      touched = true;
    }
  }
  return touched ? adv : undefined;
}

/** Filter schema shared by e-hentai and exhentai. */
const EH_SEARCH_FILTERS: SearchFiltersSchema = {
  fields: [
    {
      id: 'categories',
      type: 'chips',
      label: 'Categories',
      multi: true,
      options: ALL_CATEGORY_NAMES.map((c) => ({
        value: c,
        label:
          c === 'artistcg'
            ? 'Artist CG'
            : c === 'gamecg'
              ? 'Game CG'
              : c === 'imageset'
                ? 'Image Set'
                : c === 'asianporn'
                  ? 'Asian Porn'
                  : c === 'nonh'
                    ? 'Non-H'
                    : c.charAt(0).toUpperCase() + c.slice(1),
      })),
    },
    {
      id: 'minRating',
      type: 'select',
      label: 'Minimum rating',
      options: [
        { value: '2', label: '2★+' },
        { value: '3', label: '3★+' },
        { value: '4', label: '4★+' },
        { value: '5', label: '5★' },
      ],
    },
    { id: 'minPages', type: 'number', label: 'Min pages', min: 1 },
    { id: 'maxPages', type: 'number', label: 'Max pages', min: 1 },
    { id: 'sh', type: 'toggle', label: 'Browse expunged galleries' },
    { id: 'sr', type: 'toggle', label: 'Require gallery torrent' },
    { id: 'sfl', type: 'toggle', label: 'Disable language filter' },
    { id: 'sfu', type: 'toggle', label: 'Disable uploader filter' },
    { id: 'sft', type: 'toggle', label: 'Disable tag filter' },
    {
      id: 'includeTags',
      type: 'tags',
      label: 'Include tags',
      placeholder: 'language:english',
    },
    {
      id: 'excludeTags',
      type: 'tags',
      label: 'Exclude tags',
      placeholder: '-language:chinese',
      mode: 'exclude',
    },
  ],
};

function authCookies(auth?: SourceAuth): string {
  return serializeCookies(auth?.cookies);
}

async function getGalleryMetadata(
  site: SiteContext,
  gid: number,
  token: string,
): Promise<GalleryMetadata | undefined> {
  const key = galleryMetadataCacheKey(site.id, gid, token);
  const cached = galleryMetadataCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.row;

  const pending = galleryMetadataInFlight.get(key);
  if (pending) return pending;

  const promise = gdata([[gid, token]], site)
    .then((meta) => {
      const row = meta.gmetadata[0];
      const entry: GalleryMetadataCacheEntry = {
        expiresAt: Date.now() + GALLERY_METADATA_TTL_MS,
      };
      if (row !== undefined) entry.row = row;
      galleryMetadataCache.set(key, entry);
      return row;
    })
    .finally(() => {
      if (galleryMetadataInFlight.get(key) === promise) {
        galleryMetadataInFlight.delete(key);
      }
    });
  galleryMetadataInFlight.set(key, promise);
  return promise;
}

async function fetchHtmlList(
  url: string,
  site: SiteContext,
  cookies: string,
  currentPage: number,
): Promise<Page<ComicCard>> {
  const opts: { cookies?: string } = {};
  if (cookies) opts.cookies = cookies;
  const text = await fetchEhText(url, site, opts);
  return parseGalleryList(text, {
    source: site.id,
    baseUrl: site.host,
    currentPage,
  });
}

export function buildEhSource(buildOpts: BuildEhSourceOpts): ComicSource {
  const { site, sourceId, name, description } = buildOpts;

  async function search(q: SearchQuery, auth?: SourceAuth): Promise<Page<ComicCard>> {
    const cats = categoryNamesFromQuery(q);
    const opts: Parameters<typeof buildListUrl>[1] = {};
    if (q.q) opts.keyword = q.q;
    if (cats.length > 0) opts.category = categoriesToMask(cats);
    if (q.cursor) opts.paging = { next: q.cursor };
    else if (q.page) opts.paging = { page: q.page };
    const nonCategoryTags = (q.includeTags ?? []).filter(
      (t) => !t.toLowerCase().startsWith('category:'),
    );
    const nonCategoryExcludeTags = (q.excludeTags ?? []).filter(
      (t) => !t.toLowerCase().startsWith('category:'),
    );
    const adv = advancedFromQuery(q);
    const uploader = q.uploader?.trim();
    if (
      uploader &&
      !q.q &&
      cats.length === 0 &&
      nonCategoryTags.length === 0 &&
      nonCategoryExcludeTags.length === 0 &&
      !adv
    ) {
      const url = buildListUrl(site, { mode: 'uploader', uploader, paging: opts.paging });
      return fetchHtmlList(url, site, authCookies(auth), q.page ?? 0);
    }
    if (
      !q.q &&
      !uploader &&
      cats.length === 0 &&
      nonCategoryTags.length === 1 &&
      nonCategoryExcludeTags.length === 0 &&
      !adv
    ) {
      const tag = nonCategoryTags[0]?.trim();
      if (tag) {
        const url = buildListUrl(site, { mode: 'tag', tag, paging: opts.paging });
        return fetchHtmlList(url, site, authCookies(auth), q.page ?? 0);
      }
    }
    if (q.includeTags && q.includeTags.length > 0) {
      // Compose textual tag predicates: `tag:foo$` (exact tag-name).
      const tagPreds = nonCategoryTags.map((t) => `${t.replace(/^([^:]+):/, '$1:')}$`);
      if (tagPreds.length > 0) {
        opts.keyword = `${opts.keyword ?? ''} ${tagPreds.join(' ')}`.trim();
      }
    }
    if (q.excludeTags && q.excludeTags.length > 0) {
      // e-hentai's `f_search` exclusion syntax is `-tag:foo$`.
      const negPreds = nonCategoryExcludeTags.map((t) => `-${t.replace(/^([^:]+):/, '$1:')}$`);
      if (negPreds.length > 0) {
        opts.keyword = `${opts.keyword ?? ''} ${negPreds.join(' ')}`.trim();
      }
    }
    if (adv) opts.advanced = adv;
    const url = buildListUrl(site, opts);
    return fetchHtmlList(url, site, authCookies(auth), q.page ?? 0);
  }

  async function getPopular(
    popOpts: PopularOpts | undefined,
    auth?: SourceAuth,
  ): Promise<Page<ComicCard>> {
    const opts: Parameters<typeof buildListUrl>[1] = { mode: 'popular' };
    if (popOpts?.cursor) opts.paging = { next: popOpts.cursor };
    else if (popOpts?.page) opts.paging = { page: popOpts.page };
    const url = buildListUrl(site, opts);
    return fetchHtmlList(url, site, authCookies(auth), popOpts?.page ?? 0);
  }

  async function getRecommendations(
    recOpts: RecommendationOpts | undefined,
    auth?: SourceAuth,
  ): Promise<Page<ComicCard>> {
    const optionId =
      recOpts?.optionId ?? EH_RECOMMENDATION_OPTIONS.find((option) => option.default)?.id;
    const toplistId = optionId ? EH_TOPLIST_IDS[optionId] : undefined;
    const opts: Parameters<typeof buildListUrl>[1] =
      toplistId !== undefined ? { mode: 'toplist', toplistId } : { mode: 'popular' };
    if (recOpts?.cursor) opts.paging = { next: recOpts.cursor };
    else if (recOpts?.page) opts.paging = { page: recOpts.page };
    const url = buildListUrl(site, opts);
    return fetchHtmlList(url, site, authCookies(auth), recOpts?.page ?? 0);
  }

  async function getComic(id: string, auth?: SourceAuth): Promise<ComicDetails> {
    const { gid, token } = parseEhId(id);
    const cookies = authCookies(auth);
    const snapshot = await loadGalleryPageZero(site, gid, token, cookies);
    const details = parseGalleryDetail(snapshot.html, {
      source: sourceId,
      pageUrl: snapshot.url,
    });
    details.sourceUrl = snapshot.url;
    const pagePreviews = buildPagePreviewBatch({
      id,
      gid,
      token,
      previewPage: 0,
      site,
      html: snapshot.html,
      pageUrl: snapshot.url,
    });
    if (pagePreviews) details.pagePreviews = pagePreviews;

    if (details.pageCount <= 0) {
      try {
        const row = await getGalleryMetadata(site, gid, token);
        if (row) {
          if (row.title && !details.title) details.title = row.title;
          if (row.title_jpn && !details.altTitle) details.altTitle = row.title_jpn;
          if (row.thumb) details.coverUrl ||= publicEhImageUrl(row.thumb, site);
          if (row.filecount) {
            const fileCount = Number.parseInt(row.filecount, 10);
            if (Number.isFinite(fileCount) && fileCount > 0) details.pageCount = fileCount;
          }
          if (row.rating) {
            const rating = Number.parseFloat(row.rating);
            if (Number.isFinite(rating)) details.rating = rating;
          }
          if (row.uploader && !details.uploader) details.uploader = row.uploader;
          if (row.posted) {
            const timestamp = Number.parseInt(row.posted, 10);
            if (Number.isFinite(timestamp) && timestamp > 0) {
              details.uploadedAt = timestamp * 1000;
            }
          }
          const existingTags = new Set(details.tags.map((tag) => tag.id.toLowerCase()));
          for (const tagSpec of row.tags ?? []) {
            const separator = tagSpec.indexOf(':');
            const namespace = separator > 0 ? tagSpec.slice(0, separator) : 'misc';
            const tagName = separator > 0 ? tagSpec.slice(separator + 1) : tagSpec;
            const tagId = tagSpec.toLowerCase();
            if (existingTags.has(tagId)) continue;
            details.tags.push({ id: tagSpec, name: tagName, namespace });
          }
        }
      } catch {
        // HTML details remain usable when optional API enrichment fails.
      }
    }

    return details;
  }

  async function getPagePreviews(
    id: string,
    auth?: SourceAuth,
    opts?: { previewPage?: number },
  ): Promise<PagePreviewBatch> {
    const { gid, token } = parseEhId(id);
    const previewPage = Math.max(0, Math.trunc(opts?.previewPage ?? 0));
    const cookies = authCookies(auth);
    let url: string;
    let html: string;
    if (previewPage === 0) {
      const snapshot = await loadGalleryPageZero(site, gid, token, cookies);
      url = snapshot.url;
      html = snapshot.html;
    } else {
      const pageUrl = new URL(galleryUrl(site, gid, token));
      pageUrl.searchParams.set('p', String(previewPage));
      url = pageUrl.toString();
      const persistKey = `${site.id}:${gid}:${token}:p${previewPage}:${await galleryAuthScope(cookies)}`;
      const persisted = await readPersistedEhHtml(persistKey);
      if (persisted) {
        html = persisted.html;
      } else {
        const fetchOpts: { cookies?: string } = {};
        if (cookies) fetchOpts.cookies = cookies;
        html = await fetchEhText(url, site, fetchOpts);
        void writePersistedEhHtml(persistKey, { html, url }, GALLERY_PREVIEW_PAGE_PERSIST_TTL_MS);
      }
      primePTokenCacheFromPreviewHtml(gid, site, html);
    }
    return (
      buildPagePreviewBatch({
        id,
        gid,
        token,
        previewPage,
        site,
        html,
        pageUrl: url,
      }) ?? { items: [], previewPage, hasMore: false }
    );
  }

  async function getComments(
    id: string,
    auth?: SourceAuth,
    opts?: SourceCommentOpts,
  ): Promise<SourceCommentPage> {
    const { gid, token } = parseEhId(id);
    const page = Math.max(1, Math.trunc(opts?.page ?? 1));
    const pageSize = Math.max(1, Math.trunc(opts?.pageSize ?? 20));

    if (opts?.parentId) {
      return { items: [], page, hasMore: false, totalPages: 0, totalItems: 0 };
    }

    const snapshot = await loadGalleryPageZero(site, gid, token, authCookies(auth));
    const comments = parseGalleryComments(snapshot.html, {
      source: sourceId,
      pageUrl: snapshot.url,
    });
    const totalItems = comments.length;
    const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / pageSize);
    const start = (page - 1) * pageSize;
    const items = comments.slice(start, start + pageSize);

    return {
      items,
      page,
      hasMore: start + pageSize < totalItems,
      totalPages,
      totalItems,
    };
  }

  async function getGalleryPageCount(gid: number, token: string, cookies: string): Promise<number> {
    const snapshot = await loadGalleryPageZero(site, gid, token, cookies);
    try {
      const details = parseGalleryDetail(snapshot.html, {
        source: sourceId,
        pageUrl: snapshot.url,
      });
      if (Number.isFinite(details.pageCount) && details.pageCount > 0) {
        return details.pageCount;
      }
    } catch (error) {
      if (!(error instanceof ParseError)) throw error;
    }

    const row = await getGalleryMetadata(site, gid, token);
    const fileCount = row?.filecount ? Number.parseInt(row.filecount, 10) : 0;
    if (Number.isFinite(fileCount) && fileCount > 0) return fileCount;

    throw new ParseError(`getPages: page count not found for gid ${gid}`, { source: sourceId });
  }

  async function getPages(id: string, auth?: SourceAuth): Promise<PageRef[]> {
    const { gid, token } = parseEhId(id);
    const cookies = authCookies(auth);
    const total = await getGalleryPageCount(gid, token, cookies);
    return Array.from({ length: total }, (_, idx) => {
      const page = idx + 1;
      const extra: Record<string, unknown> = { gid, token, page };
      return {
        comicId: id,
        index: idx,
        extra,
      };
    });
  }

  async function resolvePToken(args: {
    gid: number;
    token: string;
    page: number;
    existing?: string;
    cookies?: string;
  }): Promise<string> {
    if (args.existing) return args.existing;
    return getPTokenForPageFromPreview(
      args.gid,
      args.token,
      args.page,
      site,
      args.cookies ? { cookies: args.cookies } : {},
    );
  }

  async function ensureShowKey(
    gid: number,
    page1Ptoken: string,
    cookies: string | undefined,
  ): Promise<ShowKeyCacheEntry> {
    const cached = getShowKey(sourceId, gid);
    if (cached?.showKey) return cached;

    const key = showKeyCacheKey(sourceId, gid);
    const pending = showKeyInFlight.get(key);
    if (pending) return pending;

    const promise = loadFirstPage(gid, page1Ptoken, site, cookies ? { cookies } : {})
      .then((data) => putShowKey(sourceId, gid, data))
      .finally(() => {
        if (showKeyInFlight.get(key) === promise) {
          showKeyInFlight.delete(key);
        }
      });
    showKeyInFlight.set(key, promise);
    return promise;
  }

  async function resolveImageUrl(ref: PageRef, auth?: SourceAuth): Promise<{ url: string }> {
    const { gid, token } = parseEhId(ref.comicId);
    const extra = (ref.extra ?? {}) as { ptoken?: string };
    const cookies = authCookies(auth);
    const page1Based = ref.index + 1;
    const ptoken = await resolvePToken({
      gid,
      token,
      page: page1Based,
      existing: extra.ptoken,
      cookies,
    });

    if (page1Based === 1) {
      const entry = await ensureShowKey(gid, ptoken, cookies);
      if (entry.firstPageImageUrl) {
        return { url: publicEhImageUrl(entry.firstPageImageUrl, site) };
      }
      throw new ParseError(`resolveImageUrl: first page image not available for gid ${gid}`, {
        source: sourceId,
      });
    }

    const page1Ptoken = await resolvePToken({
      gid,
      token,
      page: 1,
      cookies,
    });
    const entry = await ensureShowKey(gid, page1Ptoken, cookies);
    const pageData = await loadPage(
      { gid, page: page1Based, ptoken, showkey: entry.showKey },
      site,
    );
    return { url: publicEhImageUrl(pageData.imageUrl, site) };
  }

  async function validateAuth(
    auth: SourceAuth,
    opts?: { anonymous?: boolean },
  ): Promise<{ ok: boolean; username?: string; reason?: string }> {
    const cookies = authCookies(auth);
    const fetchOpts: { cookies?: string } = {};
    if (cookies) fetchOpts.cookies = cookies;
    if (opts?.anonymous) {
      const url = buildListUrl(site, { mode: 'popular' });
      try {
        await fetchEhText(url, site, fetchOpts);
      } catch (error) {
        if (error instanceof CloudflareChallenge || error instanceof NetworkError) {
          return { ok: false };
        }
        // Other typed upstream states prove the request cleared Cloudflare.
      }
      return { ok: true };
    }

    if (buildOpts.extraValidate) {
      const extra = await buildOpts.extraValidate(auth);
      if (extra && extra.ok === false) {
        return extra.reason !== undefined ? { ok: false, reason: extra.reason } : { ok: false };
      }
    }

    const text = await fetchEhText(site.uconfigUrl, site, fetchOpts);
    const parsed = parseUConfig(text);
    if (!parsed.loggedIn) {
      return { ok: false };
    }
    return parsed.username !== undefined ? { ok: true, username: parsed.username } : { ok: true };
  }

  async function listFavorites(auth: SourceAuth, opts: ListOpts): Promise<Page<ComicCard>> {
    const args: Parameters<typeof ehListFavorites>[2] = {};
    if (opts.category !== undefined) args.category = opts.category;
    if (opts.cursor !== undefined) args.cursor = opts.cursor;
    if (opts.page !== undefined) args.page = opts.page;
    const cookies = authCookies(auth);
    const result = await ehListFavorites(site, cookies ? { cookies } : {}, args);
    return result.page;
  }

  async function addFavorite(id: string, auth: SourceAuth, category?: number): Promise<void> {
    const { gid, token } = parseEhId(id);
    const cookies = authCookies(auth);
    await ehAddFavorite(site, { gid, token, category: category ?? 0 }, cookies ? { cookies } : {});
  }

  async function removeFavorite(id: string, auth: SourceAuth): Promise<void> {
    const { gid, token } = parseEhId(id);
    const cookies = authCookies(auth);
    await ehRemoveFavorite(site, { gid, token }, cookies ? { cookies } : {});
  }

  const source: ComicSource = {
    id: sourceId,
    name,
    description,
    capabilities: {
      login: true,
      favorites: true,
      multiFavCategories: true,
      search: true,
      advancedSearch: true,
      uploaderSearch: true,
      sortOptions: ['newest', 'popular'],
      popularRanges: ['today', 'week', 'month', 'year'],
      recommendations: true,
      comments: true,
      ...(buildOpts.loginNotes !== undefined ? { loginNotes: buildOpts.loginNotes } : {}),
    },
    searchFilters: EH_SEARCH_FILTERS,
    recommendationOptions: EH_RECOMMENDATION_OPTIONS,
    search,
    getPopular,
    getRecommendations,
    getComic,
    getPagePreviews,
    getComments,
    getPages,
    resolveImageUrl,
    validateAuth,
    listFavorites,
    addFavorite,
    removeFavorite,
  };
  return source;
}
